<template>
  <!-- Clean signin page -->
  <div v-if="route.path === '/signin'" class="min-h-screen bg-grain">
    <router-view />
  </div>

  <!-- Mobile app shell: auth + scanner focused -->
  <div v-else-if="isMobileShell" class="min-h-screen bg-grain">
    <header
      v-if="route.path !== '/signin' && route.path !== '/scan'"
      class="sticky top-0 z-[900] flex items-center justify-between border-b border-slate-700/60 bg-slate-950/95 px-4 py-3 backdrop-blur"
    >
      <div class="text-sm font-semibold tracking-wide text-slate-100">TestDrive Scanner</div>
      <button
        type="button"
        class="rounded-md border border-slate-700 bg-slate-800/70 px-3 py-1.5 text-xs text-slate-100"
        @click="signOut"
      >
        Sign Out
      </button>
    </header>
    <main :class="route.path === '/scan' ? 'px-0 pb-0 pt-0' : 'px-3 pb-6 pt-3'">
      <router-view v-slot="slot">
        <component
          :is="slot.Component"
          v-bind="routeProps"
          @reserve="openReserve"
          @checkout="openCheckout"
          @checkin="openCheckin"
          @open-reservation="openReservation"
        />
      </router-view>
    </main>
    <ToastNotifications />
  </div>

  <!-- Full app with fixed sidebar -->
  <div v-else class="min-h-screen bg-grain">
    <Sidebar
      :navigation="navigation"
      :activeTab="activeTab"
      :currentUser="currentUser"
      @setActiveTab="activeTab = $event"
      @openModal="onOpenModal"
      @signOut="signOut"
      @openAlertPreferences="showAlertPreferences = true"
      @adminSlideoutToggle="handleAdminSlideoutToggle"
      @adminToolsToggle="handleAdminToolsToggle"
    />

    <Motion class="min-h-screen flex flex-col overflow-visible" :animate="mainMotion" :transition="mainSpring">
      <main class="p-6 pt-[60px] space-y-6 ml-[120px] mr-[20px] mt-0 flex-1 overflow-y-auto">
          <router-view v-slot="slot">
            <component
              :is="slot.Component"
              v-bind="routeProps"
              @reserve="openReserve"
              @checkout="openCheckout"
              @checkin="openCheckin"
              @open-reservation="openReservation"
            />
          </router-view>

          <transition name="modal-scale" appear>
            <ReserveModal
              v-if="reserveFor"
              :equipment-id="reserveFor.id"
              @close="reserveFor = null"
              @saved="onSaved"
            />
          </transition>
          <transition name="modal-scale" appear>
            <CheckoutWizard
              v-if="checkoutFor"
              :equipment-id="checkoutFor.id"
              @close="checkoutFor = null"
              @saved="onSaved"
            />
          </transition>
          <transition name="modal-scale" appear>
            <CheckinModal
              v-if="checkinFor"
              :checkout-id="activeCheckoutId"
              @close="checkinFor = null"
              @saved="onSaved"
            />
          </transition>
          <transition name="modal-scale" appear>
            <ReservationDetailModal
              v-if="reservationDetail"
              :reservation="reservationDetail"
              @close="reservationDetail = null"
              @saved="onSaved"
            />
          </transition>
        </main>

        <!-- Modals -->
        <transition name="modal-scale" appear>
          <BookingModal v-if="showBooking" @close="closeModal" @save="saveBooking" />
        </transition>
      <EquipmentTypeOnboarding
        v-if="showEquipment"
        :open="!!showEquipment"
        :inventory-types="inventory.types"
        :products="inventory.products"
        :loading="creatingType"
        :draft-id="activeDraftId"
        @close="closeModal"
        @submit="saveEquipment"
      />
        <transition name="modal-scale" appear>
          <UserModal v-if="showUser" @close="closeModal" @save="saveUser" />
        </transition>
        <transition name="modal-scale" appear>
          <AlertPreferencesModal
            v-if="showAlertPreferences"
            @close="showAlertPreferences = false"
          />
        </transition>
    </Motion>
    <ToastNotifications />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import Sidebar from './components/Sidebar.vue'
// Removed AppWithMFA import - using direct Auth0 integration
import ReserveModal from './components/modals/ReserveModal.vue'
import CheckoutWizard from './components/modals/CheckoutWizard.vue'
import CheckinModal from './components/modals/CheckinModal.vue'
import { getOpenCheckoutId, listReservationsInRange } from './lib/appApi'
import { supabase } from './lib/supabaseClient'
import BookingModal from './components/modals/BookingModal.vue'
import EquipmentTypeOnboarding from './components/modals/EquipmentTypeOnboarding.vue'
import { uploadEquipmentAsset } from '@/services/inventoryStorage'
import UserModal from './components/modals/UserModal.vue'
import AlertPreferencesModal from './components/modals/AlertPreferencesModal.vue'
import ReservationDetailModal from './components/modals/ReservationDetailModal.vue'
import { Motion } from 'motion-v'
import { useAuthStore } from '@/stores/auth'
import { useAuth0Store } from '@/stores/auth0'
import { useInventoryStore } from '@/stores/inventory'
import type { EquipmentTypeCreateInput } from '@/types/inventory'
import { createEquipmentTypeWithDetails } from '@/lib/inventoryApi'
import ToastNotifications from './components/ToastNotifications.vue'
import { createLogger } from '@/lib/logger'
import { isActiveCheckoutStatus, isMockSerial, normalizeStatus } from '@/lib/checkoutStatus'

const log = createLogger('App')
const DATA_UPDATED_EVENT = 'testdrive:data-updated'

const route = useRoute()
const routerInst = useRouter()
const activeTab = ref('dashboard')

// Removed MFA complexity - back to simple auth

type CurrentUser = { initials: string; name: string; role: string; email?: string }
const currentUser = ref<CurrentUser>({
  initials: 'GC',
  name: 'Greg Corwin',
  role: 'Admin',
  email: 'greg@gregc.io',
})
const auth = useAuthStore()
const auth0Store = useAuth0Store()
const isMobileViewport = ref(false)

function updateViewportMode() {
  if (typeof window === 'undefined') return
  const touchViewport =
    window.matchMedia('(max-width: 1024px)').matches && (window.navigator.maxTouchPoints || 0) > 1
  isMobileViewport.value = window.matchMedia('(max-width: 900px)').matches || touchViewport
}

const isMobileShell = computed(() => {
  if (typeof window === 'undefined') return false
  const mobileUA = /android|iphone|ipad|ipod|mobile/i.test(window.navigator.userAgent || '')
  const touchViewport = (window.navigator.maxTouchPoints || 0) > 1 && window.innerWidth <= 1024
  return isMobileViewport.value || mobileUA || touchViewport
})

const isAdminSlideoutOpen = ref(false)
const isAdminToolsOpen = ref(false)

function handleAdminSlideoutToggle(open: boolean) {
  isAdminSlideoutOpen.value = open
}

function handleAdminToolsToggle(open: boolean) {
  isAdminToolsOpen.value = open
}

const mainSpring = { type: 'spring' as const, stiffness: 520, damping: 38, mass: 1.1 }
const mainMotion = computed(() => {
  if (isAdminSlideoutOpen.value || isAdminToolsOpen.value) {
    // Keep this subtle to reduce perceived flicker while preserving the flyout shift.
    return { x: 240, scale: 0.992, rotate: 0 }
  }
  return { x: 0, scale: 1, rotate: 0 }
})

/**
 * Map internal role slugs to human-friendly display labels.
 * Falls back to title-casing the slug for unknown roles.
 */
const ROLE_DISPLAY_NAMES: Record<string, string> = {
  admin: 'Admin',
  sales_engineer: 'Sales Engineer',
  lingo_partner_support: 'Partner Support',
  lingo_sales_support: 'Sales Support',
  partner: 'Partner',
  viewer: 'Viewer',
}

function displayRoleName(roles: string[]): string {
  if (!roles.length) return 'User'
  const primary = roles[0]
  return ROLE_DISPLAY_NAMES[primary] || primary.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Watch Auth0 user for sidebar display
watch(
  [() => auth0Store.user, () => auth0Store.isAdmin, () => auth0Store.userRoles],
  ([user, _isAdmin, roles]) => {
    if (user) {
      currentUser.value = {
        initials: auth0Store.initials,
        name: auth0Store.displayName,
        role: displayRoleName(roles),
        email: user.email || undefined,
      }
      log.debug('Auth0 user updated in sidebar:', user.email, 'role:', currentUser.value.role)
    } else {
      currentUser.value = {
        initials: '??',
        name: 'Guest',
        role: 'Guest',
        email: undefined,
      }
    }
  },
  { immediate: true },
)

async function signOut() {
  log.debug('Signing out with Auth0...')
  currentUser.value = { initials: '??', name: 'Guest', role: 'Guest' }
  await auth0Store.logout()
}

/**
 * Sidebar navigation items with optional permission gate.
 * Each item can require a specific permission to be visible.
 * Items without a `requiredPermission` are shown to all authenticated users.
 */
const baseNavigation: Array<{
  key: string
  name: string
  iconSrc: string
  requiredPermission?: string
}> = [
  { key: 'dashboard', name: 'Lookout', iconSrc: '/svg-icons/sidebar-lookout.svg', requiredPermission: 'view_dashboard' },
  { key: 'calendar', name: 'Calendar', iconSrc: '/svg-icons/calendar-thin.svg', requiredPermission: 'view_calendar' },
  { key: 'scan', name: 'Scan', iconSrc: '/svg-icons/sidebar-scan.svg', requiredPermission: 'scan_equipment' },
  { key: 'my-items', name: 'My Equip', iconSrc: '/svg-icons/my-equip5.svg', requiredPermission: 'view_my_equipment' },
  { key: 'picker', name: 'Picker', iconSrc: '/svg-icons/picker2.svg', requiredPermission: 'view_picker' },
  { key: 'inventory', name: 'Inventory', iconSrc: '/svg-icons/sidebar-inventory.svg', requiredPermission: 'view_inventory' },
  { key: 'consignees', name: 'Consignees', iconSrc: '/svg-icons/sidebar-consignees.svg', requiredPermission: 'checkout_equipment' },
  { key: 'docs', name: 'Docs', iconSrc: '/svg-icons/docs-thin.svg', requiredPermission: 'view_docs' },
  { key: 'off-road', name: 'Off-Road Edition', iconSrc: '/images/off-road-edition.svg' },
]

/** Filter navigation items based on the user's loaded permissions */
const navigation = computed(() => {
  return baseNavigation.filter((item) => {
    if (!item.requiredPermission) return true
    return auth0Store.hasPermission(item.requiredPermission)
  })
})
const routeProps = computed(() => {
  if (route.path === '/dashboard')
    return {
      stats: stats.value,
      recentActivity: recentActivity.value,
      scopeMode: dashboardScopeMode.value,
      selectedEngineerId: selectedDashboardEngineerId.value,
      engineers: dashboardEngineers.value,
      filters: dashboardFilters.value,
      organizations: dashboardOrganizations.value,
      purposeOptions: dashboardPurposeOptions.value,
      projectOptions: dashboardProjectOptions.value,
      spotterDetails: dashboardSpotterDetails.value,
      loading: dashboardLoading.value,
      onScopeChange: setDashboardScope,
      onEngineerChange: setDashboardEngineer,
      onFiltersChange: setDashboardFilters,
      quickActions: quickActions.value,
    }
  if (route.path === '/inventory') return {}
  if (route.path === '/picker') return {}
  if (route.path === '/my-items') return {}
  if (route.path === '/calendar')
    return {
      monthLabel: currentMonthLabel.value,
      cells: calendarCells.value,
      prev: goPrevMonth,
      next: goNextMonth,
      reservationsByDay: reservationsMap.value,
    }
  if (route.path === '/tracking') return {}
  if (route.path === '/docs') return {}
  if (route.path === '/off-road') return {}
  return {}
})
type EquipmentListItem = { id: number }
type ReservationEntry = Record<string, unknown>

const reserveFor = ref<EquipmentListItem | null>(null)
const checkoutFor = ref<EquipmentListItem | null>(null)
const checkinFor = ref<EquipmentListItem | null>(null)
const activeCheckoutId = ref<number>(0)

function openReserve(item: EquipmentListItem) {
  reserveFor.value = item
}
function openCheckout(item: EquipmentListItem) {
  checkoutFor.value = item
}
async function openCheckin(item: EquipmentListItem) {
  checkinFor.value = item
  const id = await getOpenCheckoutId(item.id)
  activeCheckoutId.value = id || 0
}
function onSaved() {
  refreshOperationalData()
  try {
    window.dispatchEvent(new CustomEvent(DATA_UPDATED_EVENT))
  } catch {}
}
function openReservation(res: ReservationEntry) {
  try {
    log.debug('openReservation', res)
  } catch {}
  reservationDetail.value = res
}

type StatCard = { label: string; value: number; change: number; color: string; iconSrc: string }
type DashboardScopeMode = 'me' | 'all' | 'engineer'
type DashboardEngineer = { userId: string; name: string | null; email: string | null }
type DashboardOrganization = { id: number; name: string }
type DashboardFilters = {
  startDate: string
  endDate: string
  organizationId: string
  purpose: string
  projectName: string
}
type ReservationDashboardRow = {
  id: number
  status: 'reserved' | 'checked-out' | 'maintenance' | 'past'
  sales_engineer_id: string | null
  created_by: string | null
  equipment_serial_id: number | null
  organization_name: string | null
  purpose: string | null
  equipment_name: string | null
  equipment_serial: string | null
  start_at: string | null
  end_at: string | null
}
type CheckoutDashboardRow = {
  checkout_id: number
  equipment_serial_id: number | null
  equipment_serial?: string | null
  ended_at: string | null
  checkout_status: string | null
  is_active_checkout?: boolean
  is_reportable_active_checkout?: boolean
  dispatch_gap?: boolean
  roadmap_status?: string | null
  outbound_legs?: number
  has_outbound_in_transit?: boolean
  has_return_in_transit?: boolean
  has_outbound_tracking?: boolean
  has_outbound_label?: boolean
  organization_id?: number | null
  contact_id?: number | null
  created_at?: string | null
  project_name?: string | null
  watchlist?: boolean | null
  expected_return_at?: string | null
}
type ShipmentDashboardRow = {
  id: number
  type: 'outbound' | 'return'
  status: string | null
  checkout_id: number | null
  created_at: string | null
}
type SpotterDetailRow = {
  checkoutId: number
  equipment: string
  serial: string
  owner: string
  status: string
  projectName: string
  watchlist: boolean
  outboundLegs: number
  hasOutboundInTransit: boolean
  dispatchGap: boolean
}
const stats = ref<StatCard[]>([
  {
    label: 'Active Reservations',
    value: 0,
    change: 0,
    color: 'bg-amber-600',
    iconSrc: '/images/status-card-available-white.svg',
  },
  {
    label: 'Active Checkouts',
    value: 0,
    change: 0,
    color: 'bg-red-600',
    iconSrc: '/images/status-card-checked-out-white.svg',
  },
  {
    label: 'Outbound In Transit',
    value: 0,
    change: 0,
    color: 'bg-primary-600',
    iconSrc: '/images/status-card-available-white.svg',
  },
  {
    label: 'Returns In Transit',
    value: 0,
    change: 0,
    color: 'bg-purple-600',
    iconSrc: '/images/status-card-checked-out-white.svg',
  },
  {
    label: 'Maintenance',
    value: 0,
    change: 0,
    color: 'bg-yellow-600',
    iconSrc: '/images/status-card-maintenance-white.svg',
  },
])

type ActivityEntry = {
  id: number | string
  equipment: string
  action: string
  user: string
  status: string
  timestamp: Date
}
const recentActivity = ref<ActivityEntry[]>([])
const dashboardScopeMode = ref<DashboardScopeMode>('all')
const selectedDashboardEngineerId = ref<string>('')
const dashboardEngineers = ref<DashboardEngineer[]>([])
const dashboardOrganizations = ref<DashboardOrganization[]>([])
const dashboardPurposeOptions = ref<string[]>(['poc', 'test', 'tech_eval', 'maintenance'])
const dashboardProjectOptions = ref<string[]>([])
const dashboardViewerUserId = ref<string | null>(null)
const dashboardFilters = ref<DashboardFilters>({
  startDate: '',
  endDate: '',
  organizationId: '',
  purpose: '',
  projectName: '',
})
const dashboardLoading = ref(false)
const dashboardSpotterDetails = ref<SpotterDetailRow[]>([])

function isShipmentInTransit(status: string | null | undefined) {
  const value = String(status || '').toLowerCase()
  if (!value) return false
  return (
    value.includes('carrier_accepted') ||
    value.includes('in_transit') ||
    value.includes('out_for_delivery') ||
    value.includes('return_carrier_scan')
  )
}

function statusToAction(status: string | null | undefined, purpose?: string | null) {
  const normalized = String(status || '').toLowerCase()
  if (normalized === 'checked-out') return 'Checked Out'
  if (normalized === 'reserved') return 'Reserved'
  if (normalized === 'maintenance') return 'Maintenance'
  if (normalized === 'past') return 'Completed'
  if (purpose === 'maintenance') return 'Maintenance'
  return 'Updated'
}

/**
 * Normalize checkout statuses into readable dashboard activity labels.
 */
function checkoutStatusToAction(status: string | null | undefined) {
  const normalized = normalizeStatus(status)
  if (normalized === 'checked-out') return 'Checked Out'
  if (normalized === 'outbound-label') return 'Outbound Label Created'
  if (normalized === 'outbound-in-transit') return 'Outbound In Transit'
  if (normalized === 'with-borrower') return 'With Borrower'
  if (normalized === 'return-initiated') return 'Return Initiated'
  if (normalized === 'return-in-transit') return 'Return In Transit'
  if (normalized === 'completed') return 'Completed'
  return 'Checkout Updated'
}

function buildEngineerNameMap() {
  const m: Record<string, string> = {}
  dashboardEngineers.value.forEach((e) => {
    const label = e.name || e.email || e.userId
    m[e.userId] = label
  })
  return m
}

async function loadDashboardEngineers() {
  if (!auth0Store.isAdmin) {
    dashboardEngineers.value = []
    return
  }
  try {
    const { data: roleRows } = await supabase
      .from('user_roles')
      .select('user_id, roles(name)')

    const salesEngineerUserIds = new Set<string>()
    ;(roleRows || []).forEach((row: any) => {
      const roleName = String(row?.roles?.name || '').toLowerCase()
      if ((roleName === 'sales_engineer' || roleName === 'admin') && row?.user_id) {
        salesEngineerUserIds.add(String(row.user_id))
      }
    })

    if (!salesEngineerUserIds.size) {
      dashboardEngineers.value = []
      return
    }

    const { data: users } = await supabase
      .from('admin_users')
      .select('user_id, name, email')
      .in('user_id', [...salesEngineerUserIds])
      .order('name')

    dashboardEngineers.value = (users || []).map((u: any) => ({
      userId: String(u.user_id),
      name: u.name ?? null,
      email: u.email ?? null,
    }))
  } catch (err) {
    log.warn('Failed loading dashboard engineer list:', err)
    dashboardEngineers.value = []
  }
}

async function resolveDashboardViewerUserId(): Promise<string | null> {
  if (dashboardViewerUserId.value) return dashboardViewerUserId.value
  if (auth0Store.supabaseUserId) {
    dashboardViewerUserId.value = auth0Store.supabaseUserId
    return dashboardViewerUserId.value
  }
  const email = String(auth0Store.user?.email || '').toLowerCase()
  if (!email) return null
  try {
    const { data: adminUser } = await supabase
      .from('admin_users')
      .select('user_id')
      .ilike('email', email)
      .maybeSingle()
    dashboardViewerUserId.value = adminUser?.user_id || null
    return dashboardViewerUserId.value
  } catch {
    return null
  }
}

async function resolveDashboardScopedUserId(): Promise<string | null> {
  const meId = await resolveDashboardViewerUserId()
  if (dashboardScopeMode.value === 'me') return meId
  if (dashboardScopeMode.value === 'engineer') return selectedDashboardEngineerId.value || null
  return null
}

function setDashboardScope(next: DashboardScopeMode) {
  if (!auth0Store.isAdmin && next !== 'me') {
    dashboardScopeMode.value = 'me'
    selectedDashboardEngineerId.value = ''
    loadDashboardData()
    return
  }
  dashboardScopeMode.value = next
  if (next !== 'engineer') {
    selectedDashboardEngineerId.value = ''
  } else if (!selectedDashboardEngineerId.value && dashboardEngineers.value.length) {
    selectedDashboardEngineerId.value = dashboardEngineers.value[0].userId
  }
  loadDashboardData()
}

function setDashboardEngineer(nextUserId: string) {
  selectedDashboardEngineerId.value = nextUserId
  if (dashboardScopeMode.value === 'engineer') {
    loadDashboardData()
  }
}

function setDashboardFilters(nextFilters: DashboardFilters) {
  dashboardFilters.value = nextFilters
  loadDashboardData()
}

function trendPercent(current: number, previous: number) {
  if (!Number.isFinite(previous) || previous <= 0) return current > 0 ? 100 : 0
  return ((current - previous) / previous) * 100
}

let lastOperationalRefreshAt = 0
function refreshOperationalData(force = false) {
  const now = Date.now()
  if (!force && now - lastOperationalRefreshAt < 500) return
  lastOperationalRefreshAt = now
  loadEquipment()
  loadReservationsForMonth()
  if (route.path === '/dashboard') {
    void loadDashboardOrganizations()
    loadDashboardData()
  }
}

function onDataUpdatedEvent() {
  refreshOperationalData()
}

function onWindowFocus() {
  refreshOperationalData()
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    refreshOperationalData()
  }
}

async function loadDashboardOrganizations() {
  try {
    const { data } = await supabase
      .schema('app' as any)
      .from('organizations')
      .select('id,name')
      .order('name')
    dashboardOrganizations.value = (data || []) as DashboardOrganization[]
  } catch {
    dashboardOrganizations.value = []
  }
}

async function loadDashboardData() {
  dashboardLoading.value = true
  try {
    const scopedUserId = await resolveDashboardScopedUserId()
    const scopeRequiresUser = dashboardScopeMode.value !== 'all'

    let reservationsQuery = supabase
      .from('reservations_v')
      .select(
        'id,status,sales_engineer_id,created_by,equipment_serial_id,organization_name,purpose,equipment_name,equipment_serial,start_at,end_at',
      )
      .in('status', ['reserved', 'checked-out', 'maintenance'])

    if (dashboardFilters.value.startDate) {
      reservationsQuery = reservationsQuery.gte('start_at', `${dashboardFilters.value.startDate}T00:00:00`)
    }
    if (dashboardFilters.value.endDate) {
      reservationsQuery = reservationsQuery.lte('end_at', `${dashboardFilters.value.endDate}T23:59:59`)
    }
    if (dashboardFilters.value.purpose) {
      reservationsQuery = reservationsQuery.eq('purpose', dashboardFilters.value.purpose)
    }
    if (dashboardFilters.value.organizationId) {
      const selectedOrg = dashboardOrganizations.value.find(
        (o) => String(o.id) === dashboardFilters.value.organizationId,
      )
      if (selectedOrg?.name) {
        reservationsQuery = reservationsQuery.eq('organization_name', selectedOrg.name)
      }
    }
    const { data: reservations, error: reservationsError } = await reservationsQuery
    if (reservationsError) throw reservationsError
    const reservationRows = (reservations || []) as unknown as ReservationDashboardRow[]

    let reservationsProjectMap: Record<number, string | null> = {}
    if (reservationRows.length) {
      const { data: reservationDetails } = await supabase
        .schema('app' as any)
        .from('reservations')
        .select('id, project_name')
        .in(
          'id',
          reservationRows.map((r) => r.id),
        )
      reservationsProjectMap = (reservationDetails || []).reduce(
        (acc: Record<number, string | null>, row: any) => {
          acc[row.id] = row.project_name ?? null
          return acc
        },
        {},
      )
    }

    let filteredReservationRows = reservationRows
    if (dashboardFilters.value.projectName) {
      filteredReservationRows = reservationRows.filter(
        (r) => (reservationsProjectMap[r.id] || '') === dashboardFilters.value.projectName,
      )
    }

    let scopedReservationRows = filteredReservationRows
    if (scopeRequiresUser && scopedUserId) {
      scopedReservationRows = filteredReservationRows.filter(
        (r) => r.sales_engineer_id === scopedUserId || r.created_by === scopedUserId,
      )
      // Some historical rows may be unattributed; avoid false-zero cards.
      if (!scopedReservationRows.length && filteredReservationRows.length) {
        scopedReservationRows = filteredReservationRows
      }
    }

    // Query stable checkout columns and count only active checkout lifecycle statuses.
    const { data: checkouts, error: checkoutsError } = await supabase
      .schema('app' as any)
      .from('checkout_state_v')
      .select(
        'checkout_id,equipment_serial_id,equipment_serial,organization_id,contact_id,project_name,watchlist,created_at,expected_return_at,ended_at,checkout_status,is_active_checkout,is_reportable_active_checkout,dispatch_gap,roadmap_status,outbound_legs,has_outbound_in_transit,has_return_in_transit,has_outbound_tracking,has_outbound_label',
      )
      .order('checkout_id', { ascending: false })
    if (checkoutsError) throw checkoutsError
    let filteredCheckoutRows = ((checkouts || []) as CheckoutDashboardRow[]).filter((c) =>
      Boolean(c?.is_reportable_active_checkout) && isActiveCheckoutStatus(c.checkout_status),
    )
    if (dashboardFilters.value.organizationId) {
      filteredCheckoutRows = filteredCheckoutRows.filter((c) => String(c.organization_id ?? '') === dashboardFilters.value.organizationId)
    }
    if (dashboardFilters.value.startDate) {
      const start = new Date(`${dashboardFilters.value.startDate}T00:00:00`).getTime()
      filteredCheckoutRows = filteredCheckoutRows.filter((c) => {
        if (!c.created_at) return true
        return new Date(c.created_at).getTime() >= start
      })
    }
    if (dashboardFilters.value.endDate) {
      const end = new Date(`${dashboardFilters.value.endDate}T23:59:59`).getTime()
      filteredCheckoutRows = filteredCheckoutRows.filter((c) => {
        if (!c.created_at) return true
        return new Date(c.created_at).getTime() <= end
      })
    }

    // Exclude obvious test/mock serials so dashboard counts align with user-facing equipment views.
    filteredCheckoutRows = filteredCheckoutRows.filter((c) => !isMockSerial(c.equipment_serial))
    const scopedSerialIds = new Set<number>(
      scopedReservationRows
        .map((r: any) => Number(r.equipment_serial_id))
        .filter((n) => Number.isFinite(n)),
    )
    let checkoutRows = filteredCheckoutRows
    if (scopeRequiresUser && scopedSerialIds.size) {
      const scopedCheckoutRows = filteredCheckoutRows.filter(
        (c) => c.equipment_serial_id != null && scopedSerialIds.has(Number(c.equipment_serial_id)),
      )
      if (scopedCheckoutRows.length) checkoutRows = scopedCheckoutRows
    }
    // Keep dashboard/spotter counts at unit granularity (one active row per serial).
    const dedupedBySerial = new Map<number, any>()
    const withoutSerial: any[] = []
    for (const row of checkoutRows) {
      const serialId = Number(row.equipment_serial_id)
      if (!Number.isFinite(serialId)) {
        withoutSerial.push(row)
        continue
      }
      if (!dedupedBySerial.has(serialId)) {
        dedupedBySerial.set(serialId, row)
      }
    }
    checkoutRows = [...dedupedBySerial.values(), ...withoutSerial]
    const checkoutIds = checkoutRows.map((c) => Number(c.checkout_id))

    let shipmentRows: ShipmentDashboardRow[] = []
    if (checkoutIds.length) {
      const { data: shipments, error: shipmentsError } = await supabase
        .schema('app' as any)
        .from('shipments')
        .select('id,type,status,checkout_id,created_at')
        .in('checkout_id', checkoutIds)
        .order('created_at', { ascending: false })
      if (shipmentsError) throw shipmentsError
      shipmentRows = (shipments || []) as unknown as ShipmentDashboardRow[]
    }

    const reservedCount = scopedReservationRows.filter((r) => r.status === 'reserved').length
    let maintenanceCount = scopedReservationRows.filter((r) => r.status === 'maintenance').length
    // Maintenance is a serial-level state; fallback to equipment_serials source of truth.
    try {
      const { count: maintenanceSerialCount } = await supabase
        .schema('app' as any)
        .from('equipment_serials')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'maintenance')
      maintenanceCount = Math.max(maintenanceCount, Number(maintenanceSerialCount || 0))
    } catch {
      // Non-fatal: keep reservation-derived value.
    }
    const activeCheckoutsCount = checkoutRows.length
    const outboundInTransitCount = checkoutRows.filter((c: any) => Boolean(c.has_outbound_in_transit)).length
    const returnsInTransitCount = checkoutRows.filter((c: any) => Boolean(c.has_return_in_transit)).length
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
    const nowMs = Date.now()
    const last7Start = nowMs - sevenDaysMs
    const prev7Start = nowMs - sevenDaysMs * 2
    const inLast7 = (value: string | null | undefined) => {
      const ts = value ? new Date(value).getTime() : NaN
      return Number.isFinite(ts) && ts >= last7Start && ts <= nowMs
    }
    const inPrev7 = (value: string | null | undefined) => {
      const ts = value ? new Date(value).getTime() : NaN
      return Number.isFinite(ts) && ts >= prev7Start && ts < last7Start
    }
    const reservationsLast7 = scopedReservationRows.filter((r) => inLast7(r.start_at)).length
    const reservationsPrev7 = scopedReservationRows.filter((r) => inPrev7(r.start_at)).length
    const checkoutsLast7 = checkoutRows.filter((c) => inLast7(c.created_at)).length
    const checkoutsPrev7 = checkoutRows.filter((c) => inPrev7(c.created_at)).length
    const outboundLast7 = checkoutRows.filter((c: any) => Boolean(c.has_outbound_in_transit) && inLast7(c.created_at)).length
    const outboundPrev7 = checkoutRows.filter((c: any) => Boolean(c.has_outbound_in_transit) && inPrev7(c.created_at)).length
    const returnsLast7 = checkoutRows.filter((c: any) => Boolean(c.has_return_in_transit) && inLast7(c.created_at)).length
    const returnsPrev7 = checkoutRows.filter((c: any) => Boolean(c.has_return_in_transit) && inPrev7(c.created_at)).length
    const maintenanceLast7 = scopedReservationRows.filter(
      (r) => r.status === 'maintenance' && inLast7(r.start_at),
    ).length
    const maintenancePrev7 = scopedReservationRows.filter(
      (r) => r.status === 'maintenance' && inPrev7(r.start_at),
    ).length

    stats.value = [
      {
        label: 'Active Reservations',
        value: reservedCount,
        change: trendPercent(reservationsLast7, reservationsPrev7),
        color: 'bg-amber-600',
        iconSrc: '/images/status-card-available-white.svg',
      },
      {
        label: 'Active Checkouts',
        value: activeCheckoutsCount,
        change: trendPercent(checkoutsLast7, checkoutsPrev7),
        color: 'bg-red-600',
        iconSrc: '/images/status-card-checked-out-white.svg',
      },
      {
        label: 'Outbound In Transit',
        value: outboundInTransitCount,
        change: trendPercent(outboundLast7, outboundPrev7),
        color: 'bg-primary-600',
        iconSrc: '/images/status-card-total-equipment-white.svg',
      },
      {
        label: 'Returns In Transit',
        value: returnsInTransitCount,
        change: trendPercent(returnsLast7, returnsPrev7),
        color: 'bg-purple-600',
        iconSrc: '/images/status-card-checked-out-white.svg',
      },
      {
        label: 'Maintenance',
        value: maintenanceCount,
        change: trendPercent(maintenanceLast7, maintenancePrev7),
        color: 'bg-yellow-600',
        iconSrc: '/images/status-card-maintenance-white.svg',
      },
    ]

    const projectNameSet = new Set<string>()
    Object.values(reservationsProjectMap).forEach((name) => {
      const trimmed = String(name || '').trim()
      if (trimmed) projectNameSet.add(trimmed)
    })
    filteredCheckoutRows.forEach((row) => {
      const trimmed = String(row?.project_name || '').trim()
      if (trimmed) projectNameSet.add(trimmed)
    })
    dashboardProjectOptions.value = [...projectNameSet].sort((a, b) => a.localeCompare(b))

    const engineerNameById = buildEngineerNameMap()
    const reservationBySerial = new Map<number, ReservationDashboardRow>()
    scopedReservationRows.forEach((r) => {
      const serialId = Number((r as any).equipment_serial_id)
      if (Number.isFinite(serialId) && !reservationBySerial.has(serialId)) {
        reservationBySerial.set(serialId, r)
      }
    })
    const shipmentByCheckout = new Map<number, ShipmentDashboardRow[]>()
    shipmentRows.forEach((s) => {
      const checkoutId = Number(s.checkout_id)
      if (!Number.isFinite(checkoutId)) return
      const bucket = shipmentByCheckout.get(checkoutId) || []
      bucket.push(s)
      shipmentByCheckout.set(checkoutId, bucket)
    })
    dashboardSpotterDetails.value = checkoutRows.map((c) => {
      const serialId = Number(c.equipment_serial_id)
      const reservation = Number.isFinite(serialId) ? reservationBySerial.get(serialId) : undefined
      const ownerId = reservation?.sales_engineer_id || reservation?.created_by || ''
      const ownerLabel = ownerId
        ? engineerNameById[ownerId] ||
          (ownerId === auth0Store.supabaseUserId ? auth0Store.displayName : ownerId)
        : 'Unknown owner'
      const shipmentList = shipmentByCheckout.get(Number(c.checkout_id)) || []
      const outboundLegs = Number(c.outbound_legs || 0)
      const hasOutboundInTransit = Boolean(c.has_outbound_in_transit)
      return {
        checkoutId: Number(c.checkout_id),
        equipment: reservation?.equipment_name || `Checkout ${c.checkout_id}`,
        serial: reservation?.equipment_serial || String(c.equipment_serial_id || '—'),
        owner: ownerLabel,
        status: String(c.checkout_status || 'unknown'),
        projectName: String(c.project_name || ''),
        watchlist: Boolean(c.watchlist),
        outboundLegs,
        hasOutboundInTransit,
        dispatchGap: Boolean(c.dispatch_gap),
      }
    })

    const reservationActivity = scopedReservationRows.map((r) => ({
      id: `res-${r.id}`,
      equipment: r.equipment_name || r.equipment_serial || `Reservation ${r.id}`,
      action: statusToAction(r.status, r.purpose),
      user: r.sales_engineer_id
        ? engineerNameById[r.sales_engineer_id] ||
          (r.sales_engineer_id === auth0Store.supabaseUserId
            ? auth0Store.displayName
            : r.sales_engineer_id)
        : r.created_by && r.created_by === auth0Store.supabaseUserId
          ? auth0Store.displayName
        : 'Unknown user',
      status: r.status || 'unknown',
      timestamp: new Date(r.start_at || r.end_at || new Date().toISOString()),
    }))

    const checkoutActivity = filteredCheckoutRows.map((c) => {
      const serialId = Number(c.equipment_serial_id)
      const reservation = Number.isFinite(serialId) ? reservationBySerial.get(serialId) : undefined
      const ownerId = reservation?.sales_engineer_id || reservation?.created_by || ''
      const ownerLabel = ownerId
        ? engineerNameById[ownerId] ||
          (ownerId === auth0Store.supabaseUserId ? auth0Store.displayName : ownerId)
        : 'Unknown user'
      return {
        id: `co-${c.checkout_id}`,
        equipment: reservation?.equipment_name || reservation?.equipment_serial || `Checkout ${c.checkout_id}`,
        action: checkoutStatusToAction(c.checkout_status),
        user: ownerLabel,
        status: String(c.checkout_status || 'unknown'),
        timestamp: new Date(c.created_at || new Date().toISOString()),
      }
    })

    recentActivity.value = [...reservationActivity, ...checkoutActivity]
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, 12)
  } catch (err) {
    log.warn('Failed loading dashboard data:', err)
  } finally {
    dashboardLoading.value = false
  }
}

watch(
  [
    () => auth0Store.supabaseUserId,
    () => auth0Store.user?.email,
    () => auth0Store.isAdmin,
    dashboardScopeMode,
    selectedDashboardEngineerId,
  ],
  async ([nextSupabaseId]) => {
    dashboardViewerUserId.value = nextSupabaseId || null
    if (!auth0Store.isAdmin && dashboardScopeMode.value !== 'me') {
      dashboardScopeMode.value = 'me'
      selectedDashboardEngineerId.value = ''
    }
    if (auth0Store.isAdmin) {
      await loadDashboardEngineers()
      if (
        dashboardScopeMode.value === 'engineer' &&
        !selectedDashboardEngineerId.value &&
        dashboardEngineers.value.length
      ) {
        selectedDashboardEngineerId.value = dashboardEngineers.value[0].userId
      }
    }
    loadDashboardData()
  },
)

watch(
  () => auth0Store.isAdmin,
  (isAdmin, wasAdmin) => {
    if (!isAdmin) {
      dashboardScopeMode.value = 'me'
      selectedDashboardEngineerId.value = ''
      return
    }
    if (!wasAdmin) {
      dashboardScopeMode.value = 'all'
    }
  },
  { immediate: true },
)

type QuickAction = {
  name: string
  description: string
  color: string
  icon: string
  handler: () => void
}
const quickActions = ref<QuickAction[]>([
  {
    name: 'New Booking',
    description: 'Create a new reservation',
    color: 'bg-primary-600',
    icon: 'M12 4v16M4 12h16',
    handler: () => onOpenModal('booking'),
  },
  {
    name: 'Add Equipment',
    description: 'Register new gear',
    color: 'bg-purple-600',
    icon: 'M12 4v16M4 12h16',
    handler: () => onOpenModal('equipment'),
  },
])

type EquipmentRow = { id: number }
const equipment = ref<EquipmentRow[]>([])
const eqError = ref('')

async function loadEquipment() {
  const { data, error } = await supabase.from('equipment_v').select('*').order('name')
  if (error) {
    eqError.value = error.message
  } else {
    eqError.value = ''
    equipment.value = (data as EquipmentRow[]) || []
  }
}

const calendarCursor = ref(new Date())
const currentMonthLabel = ref('')
type CalendarCell = { key: string; date: Date; inCurrentMonth: boolean; isToday: boolean }
const calendarCells = ref<CalendarCell[]>([])

function buildCalendarGrid(base: Date) {
  const year = base.getFullYear()
  const month = base.getMonth()
  const firstOfMonth = new Date(year, month, 1)
  const startDay = firstOfMonth.getDay()
  const startDate = new Date(year, month, 1 - startDay)
  const cells: Array<{ key: string; date: Date; inCurrentMonth: boolean; isToday: boolean }> = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(startDate)
    d.setDate(startDate.getDate() + i)
    const inCurrentMonth = d.getMonth() === month
    const isToday = d.toDateString() === new Date().toDateString()
    cells.push({ key: d.toISOString(), date: d, inCurrentMonth, isToday })
  }
  calendarCells.value = cells
  const formatter = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' })
  currentMonthLabel.value = formatter.format(firstOfMonth)
}

function goPrevMonth() {
  const d = new Date(calendarCursor.value)
  d.setMonth(d.getMonth() - 1)
  calendarCursor.value = d
  buildCalendarGrid(d)
  loadReservationsForMonth()
}

function goNextMonth() {
  const d = new Date(calendarCursor.value)
  d.setMonth(d.getMonth() + 1)
  calendarCursor.value = d
  buildCalendarGrid(d)
  loadReservationsForMonth()
}

// Removed complex MFA checking - back to simple approach

buildCalendarGrid(calendarCursor.value)
onMounted(async () => {
  log.debug('App mounted, waiting for Auth0...')

  // Wait for Auth0 to be ready before loading data
  let attempts = 0
  while (auth0Store.isLoading && attempts < 50) {
    await new Promise((resolve) => setTimeout(resolve, 100))
    attempts++
  }

  log.debug('Auth0 ready, loading data...')
  loadEquipment()
  await loadDashboardOrganizations()
  await loadDashboardEngineers()
  loadDashboardData()
  window.addEventListener(DATA_UPDATED_EVENT, onDataUpdatedEvent)
  window.addEventListener('focus', onWindowFocus)
  document.addEventListener('visibilitychange', onVisibilityChange)
  updateViewportMode()
  window.addEventListener('resize', updateViewportMode)
})
loadReservationsForMonth()
loadDashboardData()

onBeforeUnmount(() => {
  window.removeEventListener(DATA_UPDATED_EVENT, onDataUpdatedEvent)
  window.removeEventListener('focus', onWindowFocus)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  window.removeEventListener('resize', updateViewportMode)
})

watch(
  () => route.path,
  (nextPath) => {
    if (nextPath === '/dashboard' || nextPath === '/consignees') {
      refreshOperationalData(true)
    }
  },
)

type ReservationRow = {
  start_at: string
  end_at: string
} & ReservationEntry

type ReservationWithStatus = ReservationRow & {
  status: 'reserved' | 'checked-out' | 'maintenance'
  _isStart: boolean
  _isEnd: boolean
}

async function loadReservationsForMonth() {
  const year = calendarCursor.value.getFullYear()
  const month = calendarCursor.value.getMonth()
  const start = new Date(year, month, 1)
  const end = new Date(year, month + 1, 0, 23, 59, 59)
  const rows = (await listReservationsInRange(
    start.toISOString(),
    end.toISOString(),
  )) as ReservationRow[]
  const reservationSerials = Array.from(
    new Set(
      rows
        .map((row: any) => String(row?.equipment_serial || '').trim())
        .filter(Boolean),
    ),
  )
  const serialStatusMap = new Map<string, string>()
  if (reservationSerials.length) {
    try {
      const { data: serialRows } = await supabase
        .schema('app' as any)
        .from('equipment_serials')
        .select('serial,status')
        .in('serial', reservationSerials)
      ;(serialRows || []).forEach((row: any) => {
        const key = String(row?.serial || '').trim()
        const value = String(row?.status || '').toLowerCase().replace(/_/g, '-')
        if (key && value) serialStatusMap.set(key, value)
      })
    } catch {
      // Non-fatal: fallback to reservations_v status only.
    }
  }
  // Project into map keyed by date for Calendar component.
  // Use the authoritative status from reservations_v (derived from
  // equipment_serials.status) instead of recomputing from time ranges.
  const map: Record<string, ReservationWithStatus[]> = {}
  rows.forEach((r) => {
    const rawStatus = String(r.status || '').toLowerCase().replace(/_/g, '-')
    const serial = String((r as any).equipment_serial || '').trim()
    const serialStatus = serialStatusMap.get(serial) || ''
    const effectiveStatus =
      rawStatus === 'past' && serialStatus === 'checked-out' ? 'checked-out' : rawStatus
    const status =
      effectiveStatus === 'checked-out' || effectiveStatus === 'reserved' || effectiveStatus === 'maintenance'
        ? (effectiveStatus as 'reserved' | 'checked-out' | 'maintenance')
        : effectiveStatus === 'past'
          ? 'past'
          : null
    if (!status || status === 'past') return
    const rs = new Date(r.start_at)
    const re = new Date(r.end_at)
    const day = new Date(rs)
    while (day <= re) {
      const key = day.toDateString()
      if (!map[key]) map[key] = []
      map[key].push({
        ...r,
        status: status as 'reserved' | 'checked-out' | 'maintenance',
        _isStart: day.toDateString() === rs.toDateString(),
        _isEnd: day.toDateString() === re.toDateString(),
      })
      day.setDate(day.getDate() + 1)
    }
  })
  reservationsMap.value = map
}

const reservationsMap = ref<Record<string, ReservationWithStatus[]>>({})
const reservationDetail = ref<ReservationEntry | null>(null)

const showBooking = ref(false)
const showEquipment = ref(false)
const creatingType = ref(false)
const inventory = useInventoryStore()
const showUser = ref(false)
const showAlertPreferences = ref(false)
const activeDraftId = ref<number | null>(null)

function onOpenModal(type: string) {
  if (type === 'booking') showBooking.value = true
  else if (type === 'equipment') showEquipment.value = true
  else if (type === 'user') showUser.value = true
}


function closeModal() {
  showBooking.value = false
  showEquipment.value = false
  showUser.value = false
  activeDraftId.value = null
}

function notify(message: string, type: 'success' | 'error' = 'success') {
  try {
    window.dispatchEvent(new CustomEvent('toast', { detail: { type, message } }))
  } catch {}
}

function formatError(error: unknown, fallback = 'Unexpected error') {
  if (error instanceof Error) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const msg = (error as { message?: string }).message
    if (msg) return msg
  }
  return fallback
}

async function saveEquipment(payload: EquipmentTypeCreateInput) {
  if (creatingType.value) return
  creatingType.value = true
  try {
    await createEquipmentTypeWithDetails(payload)
    await inventory.loadTypes()
    notify('Equipment type created')
    closeModal()
  } catch (error) {
    let message = formatError(error)
    log.error('Create equipment type failed', error)
    if ((error as any)?.code === '23505') {
      message =
        'An equipment type with this make and model already exists. Update the descriptor or edit the existing entry.'
    }
    notify(message, 'error')
    throw new Error(message)
  } finally {
    creatingType.value = false
  }
}

async function saveEquipmentDraft(payload: EquipmentTypeCreateInput) {
  if (creatingType.value) return
  creatingType.value = true
  try {
    const draftPayload = {
      id: activeDraftId.value ?? undefined,
      name: payload.name,
      make: payload.make,
      model: payload.model,
      descriptor: payload.descriptor,
      categoryId: payload.categoryId,
      isAccessory: payload.isAccessory,
      products: payload.products,
      specs: payload.specs,
      assets: payload.assets,
      accessories: payload.accessories,
      progressStep: currentStepKey.value ?? 'core',
    }
    const draft = await inventory.saveDraft(draftPayload)
    activeDraftId.value = draft.id
    notify('Draft saved')
    return { id: draft.id }
  } catch (error) {
    let message = formatError(error)
    log.error('Save equipment draft failed', error)
    if ((error as any)?.code === '23505') {
      message =
        'A draft with this make and model already exists. Update the descriptor or edit the existing type.'
    }
    notify(message, 'error')
    throw new Error(message)
  } finally {
    creatingType.value = false
  }
}

async function handleAssetUpload(payload: {
  typeKey: string
  assetType: 'front_image' | 'back_image' | 'datasheet'
  file: File
}) {
  try {
    const result = await uploadEquipmentAsset(payload)
    if (!result.publicUrl || !result.path) {
      throw new Error('Upload succeeded but storage did not return a URL')
    }
    return result
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    notify(message, 'error')
    throw error
  }
}

function saveBooking(payload: { equipment: string; user: string; start: string; end: string }) {
  recentActivity.value.unshift({
    id: Date.now(),
    equipment: payload.equipment,
    action: `Booked ${payload.start} → ${payload.end}`,
    user: payload.user,
    status: 'reserved',
    timestamp: new Date(),
  })
  closeModal()
}

function saveUser(payload: { name: string; role: string; initials: string }) {
  currentUser.value = { initials: payload.initials, name: payload.name, role: payload.role }
  closeModal()
}

// Tooltips use group-hover transitions, no JS needed
const currentStepKey = computed(() => inventory.onboardingStepKey)
</script>

<style scoped>
.tooltip {
  opacity: 0;
  transform: translate(calc(-50% + 30px), -2px) scale(0.94);
  transition:
    opacity 140ms var(--ease),
    transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1.2);
}
.group:hover .tooltip {
  opacity: 1;
  transform: translate(calc(-50% + 30px), -6px) scale(1.04);
}
</style>
